import type { Brand } from "@staffsmith/api-types";
import { importPage, PRODUCT_NAME } from "@staffsmith/web-auth";
import { applyBrand, railFolded, readLook, saveLook } from "@staffsmith/theme";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, type RouteObject, useNavigate, useRouteError } from "react-router";
import { useServices } from "./context.js";
import { QuickAddMenu, QuickAddProvider } from "./Activities.js";
import { Icon } from "./icons.js";
import { NarrowMenu, NavEntries } from "./Nav.js";
import type { NavEntry } from "./nav-model.js";
import { SignInPage } from "./pages/SignIn.js";

// Each page loads only when first visited (ADR 0003: route-level code splitting).
// A page whose code went with an old deploy reloads the tab or says a new version is out (C1).
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await importPage(load))?.default ?? (() => null),
});

// The firm's own mark beside the product name (and beside, never instead of,
// the Demo tag): the square mark at 30px beside the firm's name, which wraps
// to two lines and then ellipsizes, never clips. The full logo is used only
// when no name is shown (Nathan, Nov 4, 2026). Both are drawn only as images,
// so nothing in them runs.
const svgSrc = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

function FirmMark({ brand, name }: { brand: Brand; name: string }) {
  const shown = brand.short_name ?? name;
  if (!shown)
    return (
      <span className="firm-mark">
        {brand.logo_svg ? (
          <img className="firm-logo" src={svgSrc(brand.logo_svg)} alt="" height={30} />
        ) : brand.mark_svg ? (
          <img className="firm-square" src={svgSrc(brand.mark_svg)} alt="" width={30} height={30} />
        ) : null}
      </span>
    );
  return (
    <span className="firm-mark" title={shown}>
      {brand.mark_svg ? (
        <img className="firm-square" src={svgSrc(brand.mark_svg)} alt="" width={30} height={30} />
      ) : null}
      <span className="firm-name">{shown}</span>
    </span>
  );
}

const initials = (name: string | undefined) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

// The firm's colors as tokens (design v0.9, V1), once the sign-in brings them.
function BrandTokens({ brand }: { brand: Brand | null | undefined }) {
  const primary = brand?.primary_color;
  const accent = brand?.accent_color;
  useEffect(() => {
    applyBrand({ primary_color: primary ?? null, accent_color: accent ?? null });
  }, [primary, accent]);
  return null;
}

function Layout() {
  const { api, auth } = useServices();
  const navigate = useNavigate();
  // Folded to icons below 1366px unless the person chose (design v0.9.1, V9).
  const [look, setLook] = useState(readLook);
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const railCollapsed = railFolded(look, width);
  // A one-time hint on a small screen (V9): Compact density makes room.
  const [hint, setHint] = useState(() => {
    try {
      return (
        localStorage.getItem("staffsmith.compact-hint") !== "seen" &&
        readLook().density !== "compact" &&
        (window.innerWidth < 1440 || window.innerHeight < 800)
      );
    } catch {
      return false;
    }
  });
  const dismissHint = () => {
    try {
      localStorage.setItem("staffsmith.compact-hint", "seen");
    } catch {
      /* a private window: it shows again next time */
    }
    setHint(false);
  };
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: auth.isSignedIn() });
  // Features an operator turned off for this firm leave the menu (Phase 18).
  const features = useQuery({
    queryKey: ["features"],
    queryFn: api.features,
    enabled: auth.isSignedIn(),
  });
  const on = (flag: string) => features.data?.[flag] !== false;
  // The status page (Phase 19): a live incident StaffSmith has told firms about.
  const status = useQuery({
    queryKey: ["platform-status"],
    queryFn: api.platformStatus,
    enabled: auth.isSignedIn(),
    // On load and focus, never on a timer: a timer would keep the database awake.
  });
  // The bar's counts: decisions waiting, conversations waiting for a reply, and
  // duplicate pairs to review. Read on load and focus only, like the rest.
  const pending = useQuery({
    queryKey: ["decisions", "pending"],
    queryFn: () => api.decisions({ state: "pending" }),
    enabled: auth.isSignedIn(),
  });
  const threads = useQuery({
    queryKey: ["threads", "open"],
    queryFn: () => api.threads(),
    enabled: auth.isSignedIn(),
  });
  const pairs = useQuery({
    queryKey: ["duplicates"],
    queryFn: api.duplicates,
    enabled: auth.isSignedIn(),
  });
  const items = useQuery({
    queryKey: ["inbox-items"],
    queryFn: api.inboxItems,
    enabled: auth.isSignedIn(),
  });
  // My tasks' count: overdue and due today (design v0.7, C12b).
  const taskCount = useQuery({
    queryKey: ["task-summary"],
    queryFn: api.taskSummary,
    enabled: auth.isSignedIn(),
  });
  if (!auth.isSignedIn()) return <Navigate to="/sign-in" replace />;
  const brand = me.data?.tenant.brand;
  const roles = me.data?.roles ?? [];
  const admin = roles.includes("admin");
  const entries: NavEntry[] = [
    {
      kind: "link",
      item: { to: "/decisions", label: "Decisions", count: pending.data?.data.length ?? 0 },
    },
    {
      kind: "link",
      item: {
        to: "/inbox",
        label: "Inbox",
        // Conversations waiting for a reply, plus items for this person (C4).
        count:
          (threads.data?.data.filter((t) => t.awaiting_reply).length ?? 0) +
          (items.data?.data.length ?? 0),
      },
    },
    {
      kind: "link",
      item: {
        to: "/tasks",
        label: "My tasks",
        count: (taskCount.data?.overdue ?? 0) + (taskCount.data?.today ?? 0),
      },
    },
    {
      kind: "menu",
      group: {
        id: "recruiting",
        label: "Recruiting",
        items: [
          { to: "/jobs", label: "Jobs", description: "Job orders and their pipelines" },
          { to: "/candidates", label: "Candidates", description: "Everyone in your talent pool" },
          {
            to: "/search",
            label: "Find candidates",
            description: "Search by skills and meaning",
            show: on("candidate_search"),
          },
          {
            to: "/duplicates",
            label: "Duplicates",
            description: "Review likely duplicate candidates",
            count: pairs.data?.data.length ?? 0,
          },
          {
            to: "/interviews",
            label: "Interviews",
            description: "Mine and the team's, by day",
          },
          {
            to: "/pipelines",
            label: "Pipelines",
            description: "Benches for the roles the firm fills again and again",
          },
          {
            to: "/role-profiles",
            label: "Role profiles",
            description: "Roles the firm fills again and again",
          },
          {
            to: "/skills",
            label: "Skills library",
            description: "The skills jobs and candidates are matched on",
          },
        ],
      },
    },
    {
      kind: "menu",
      group: {
        id: "clients",
        label: "Clients",
        items: [
          { to: "/companies", label: "Companies", description: "Client accounts and prospects" },
          {
            to: "/leads",
            label: "Leads",
            description: "People and companies not yet worked, to qualify",
          },
          {
            to: "/opportunities",
            label: "Opportunities",
            description: "New business by stage, with the weighted forecast",
          },
          {
            to: "/contacts",
            label: "Contacts",
            description: "The people at your clients, and where they've moved",
          },
          {
            to: "/agreements",
            label: "Agreements",
            description: "Each client's agreements, documents and key dates",
          },
          { to: "/placements", label: "Placements", description: "Consultants on assignment" },
          {
            to: "/extensions",
            label: "Extensions",
            description: "Placements ending soon, and whether they'll extend",
          },
          {
            to: "/vms",
            label: "VMS",
            description: "Client VMS programs and submittals",
            show: on("vms"),
          },
        ],
      },
    },
    {
      kind: "menu",
      group: {
        id: "back-office",
        label: "Back office",
        items: [
          {
            to: "/onboarding",
            label: "Onboarding",
            description: "Get placed consultants cleared to start",
          },
          { to: "/exports", label: "Time exports", description: "Approved time for invoicing" },
          {
            to: "/invoicing",
            label: "Invoicing",
            description: "Invoices from approved time, approved and sent through QuickBooks",
            show: roles.some((r) => r === "admin" || r === "finance" || r === "account_manager"),
          },
          {
            to: "/payroll",
            label: "Payroll",
            description: "Pay batches and off-cycle payments",
            show: on("payroll") && roles.some((r) => r === "admin" || r === "finance"),
          },
        ],
      },
    },
    { kind: "link", item: { to: "/reports", label: "Reports" } },
    {
      kind: "menu",
      group: {
        id: "ai",
        label: "AI",
        items: [
          {
            to: "/agents",
            label: "AI agents",
            description: "What each assistant costs, and how often it's accepted",
          },
          {
            to: "/agent-activity",
            label: "Agent activity",
            description: "Every AI action and the decision it led to",
          },
          {
            to: "/writing-style",
            label: "Writing style",
            description: "Style guides and suggested changes",
          },
          { to: "/templates", label: "Templates", description: "Message and document templates" },
          {
            to: "/sequences",
            label: "Sequences",
            description: "Multi-step outreach that stops on a reply",
            show: on("sequences"),
          },
        ],
      },
    },
  ];
  const adminMenu: NavEntry[] = [
    {
      kind: "menu",
      group: {
        id: "admin",
        label: "⚙ Admin",
        items: [
          { to: "/admin/team", label: "Team and roles", description: "Staff sign-ins and roles" },
          {
            to: "/admin/integrations",
            label: "Integrations",
            description: "Microsoft 365, and soon QuickBooks and the career site",
          },
          {
            to: "/admin/kpis",
            label: "KPIs",
            description: "The KPI library: which your firm uses, and what you call them",
          },
          {
            to: "/admin/careers",
            label: "Careers",
            description: "The career site: its address, intro and published jobs",
          },
          {
            to: "/admin/branding",
            label: "Branding",
            description: "Your firm's name, colors, logo and mark",
          },
          {
            to: "/admin/tuning",
            label: "Matching tuning",
            description: "Proposed changes to how skills are weighed",
          },
          {
            to: "/admin/customization",
            label: "Customization",
            description: "Fields, pick lists and the change history",
          },
          {
            to: "/admin/templates",
            label: "Writing settings",
            description: "Personal styles, learning from edits, and who approves templates",
          },
          { to: "/import", label: "Import", description: "Bring in candidates and companies" },
          { to: "/privacy", label: "Privacy", description: "People's data requests" },
          { to: "/admin/api", label: "API", description: "Keys and webhooks for partner systems" },
          {
            to: "/admin/support",
            label: "Support and billing",
            description: "Help, support access and invoices",
          },
          {
            to: "/admin/demo",
            label: "Demo tenant",
            description: "Reset the demo to its starting state",
            show: Boolean(me.data?.tenant.demo),
          },
        ].map((i) => ({ ...i, show: admin && i.show !== false })),
      },
    },
  ];
  return (
    <QuickAddProvider>
      <div className={railCollapsed ? "shell rail-shell rail-collapsed" : "shell rail-shell"}>
        <BrandTokens brand={brand} />
        {/* The sidebar (design v0.9, V4): the firm's brand, woven, with an accent edge. */}
        <aside className="rail weave accent-edge-right">
          <nav className="nav" aria-label="Main">
            <div className="rail-top">
              {brand ? (
                <FirmMark brand={brand} name={me.data?.tenant.name ?? ""} />
              ) : (
                <span className="firm-mark">{me.data?.tenant.name ?? PRODUCT_NAME}</span>
              )}
              <span className="brand rail-label">{PRODUCT_NAME}</span>
              {me.data?.tenant.demo ? (
                <span className="badge demo" title="Synthetic data only">
                  Demo
                </span>
              ) : null}
            </div>
            <NavEntries entries={entries} />
            <QuickAddMenu />
            <div className="rail-bottom">
              <NavEntries entries={adminMenu} align="right" />
              <Link className="rail-user" to="/profile" title="My profile and settings">
                <span className="avatar" aria-hidden="true">
                  {initials(me.data?.display_name)}
                </span>
                <span className="rail-label nav-user">
                  {me.data?.display_name ?? ""}
                  <small>{roles.map((r) => r.replaceAll("_", " ")).join(", ")}</small>
                </span>
              </Link>
              <button
                type="button"
                className="link rail-signout"
                onClick={() => {
                  auth.signOut();
                  void navigate("/sign-in");
                }}
              >
                <span className="rail-label">Sign out</span>
              </button>
              <button
                type="button"
                className="link rail-fold"
                aria-label={railCollapsed ? "Expand the sidebar" : "Fold the sidebar to icons"}
                onClick={() => setLook(saveLook({ railCollapsed: !railCollapsed }))}
              >
                <Icon name={railCollapsed ? "unfold" : "fold"} />
              </button>
            </div>
          </nav>
        </aside>
        {/* Below 1024px: a brand bar with the Menu sheet instead. */}
        <header className="topbar weave accent-edge-bottom">
          {brand ? (
            <FirmMark brand={brand} name={me.data?.tenant.name ?? ""} />
          ) : (
            <span className="firm-mark">{PRODUCT_NAME}</span>
          )}
          {me.data?.tenant.demo ? <span className="badge demo">Demo</span> : null}
          <span className="spacer" />
          <NarrowMenu entries={[...entries, ...adminMenu]} />
        </header>
        <main>
          {status.data && !status.data.operational
            ? status.data.incidents
                .filter((i) => i.status !== "resolved")
                .map((i) => (
                  <p key={i.id} className="notice" role="status">
                    StaffSmith status: {i.public_summary}
                  </p>
                ))
            : null}
          {hint ? (
            <p className="notice row" role="status">
              <span>
                Short on space? Try Compact density in{" "}
                <Link to="/profile" onClick={dismissHint}>
                  your settings
                </Link>
                .
              </span>
              <button type="button" className="link" onClick={dismissHint}>
                Dismiss
              </button>
            </p>
          ) : null}
          <Outlet />
        </main>
      </div>
    </QuickAddProvider>
  );
}

// What a page that failed shows, in place of the page.
function PageError() {
  const error = useRouteError();
  return (
    <section className="page">
      <h1>Something went wrong on this page</h1>
      <p className="error" role="alert">
        {error instanceof Error ? error.message : "It couldn't be shown."}
      </p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload the page
      </button>
    </section>
  );
}

export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/",
    element: <Layout />,
    children: [
      // A page that fails shows its error here, inside the shell, rather than
      // taking the sidebar down with it.
      {
        errorElement: <PageError />,
        children: [
          { index: true, element: <Navigate to="/decisions" replace /> },
          { path: "decisions", lazy: page(() => import("./pages/Decisions.js")) },
          { path: "tasks", lazy: page(() => import("./pages/Tasks.js")) },
          { path: "profile", lazy: page(() => import("./pages/Profile.js")) },
          {
            path: "profile/connected",
            lazy: page(async () => ({
              default: (await import("./pages/Profile.js")).ConnectedPage,
            })),
          },
          { path: "admin/integrations", lazy: page(() => import("./pages/Integrations.js")) },
          {
            path: "admin/integrations/quickbooks",
            lazy: page(async () => ({
              default: (await import("./pages/Integrations.js")).QuickBooksReturnPage,
            })),
          },
          { path: "decisions/:decisionId", lazy: page(() => import("./pages/Decision.js")) },
          { path: "agent-activity", lazy: page(() => import("./pages/AgentActivity.js")) },
          { path: "admin/api", lazy: page(() => import("./pages/ApiAccess.js")) },
          { path: "admin/team", lazy: page(() => import("./pages/Team.js")) },
          { path: "admin/customization", lazy: page(() => import("./pages/Customization.js")) },
          { path: "admin/tuning", lazy: page(() => import("./pages/Tuning.js")) },
          { path: "admin/branding", lazy: page(() => import("./pages/Branding.js")) },
          { path: "admin/careers", lazy: page(() => import("./pages/CareersAdmin.js")) },
          { path: "admin/kpis", lazy: page(() => import("./pages/Kpis.js")) },
          { path: "admin/demo", lazy: page(() => import("./pages/DemoTenant.js")) },
          { path: "admin/templates", lazy: page(() => import("./pages/TemplatesAdmin.js")) },
          { path: "import", lazy: page(() => import("./pages/Import.js")) },
          { path: "templates", lazy: page(() => import("./pages/Templates.js")) },
          {
            path: "templates/:templateId",
            lazy: page(async () => ({
              default: (await import("./pages/Templates.js")).TemplatePage,
            })),
          },
          { path: "writing-style", lazy: page(() => import("./pages/WritingStyle.js")) },
          { path: "jobs", lazy: page(() => import("./pages/Jobs.js")) },
          { path: "jobs/:jobId", lazy: page(() => import("./pages/Job.js")) },
          { path: "candidates", lazy: page(() => import("./pages/Candidates.js")) },
          { path: "search", lazy: page(() => import("./pages/Search.js")) },
          { path: "duplicates", lazy: page(() => import("./pages/Duplicates.js")) },
          { path: "candidates/:candidateId", lazy: page(() => import("./pages/Candidate.js")) },
          { path: "companies", lazy: page(() => import("./pages/Companies.js")) },
          { path: "companies/:companyId", lazy: page(() => import("./pages/Company.js")) },
          { path: "leads", lazy: page(() => import("./pages/Leads.js")) },
          { path: "leads/:leadId", lazy: page(() => import("./pages/Lead.js")) },
          { path: "opportunities", lazy: page(() => import("./pages/Opportunities.js")) },
          {
            path: "opportunities/:opportunityId",
            lazy: page(() => import("./pages/Opportunity.js")),
          },
          { path: "contacts", lazy: page(() => import("./pages/Contacts.js")) },
          { path: "agreements", lazy: page(() => import("./pages/Agreements.js")) },
          { path: "agreements/:agreementId", lazy: page(() => import("./pages/Agreement.js")) },
          { path: "extensions", lazy: page(() => import("./pages/Extensions.js")) },
          { path: "skills", lazy: page(() => import("./pages/Skills.js")) },
          { path: "interviews", lazy: page(() => import("./pages/InterviewsCalendar.js")) },
          { path: "pipelines", lazy: page(() => import("./pages/Pipelines.js")) },
          { path: "pipelines/:pipelineId", lazy: page(() => import("./pages/Pipeline.js")) },
          { path: "role-profiles", lazy: page(() => import("./pages/RoleProfiles.js")) },
          {
            path: "role-profiles/:roleProfileId",
            lazy: page(() => import("./pages/RoleProfile.js")),
          },
          { path: "contacts/:contactId", lazy: page(() => import("./pages/Contact.js")) },
          { path: "agents", lazy: page(() => import("./pages/Agents.js")) },
          { path: "placements", lazy: page(() => import("./pages/Placements.js")) },
          { path: "placements/:placementId", lazy: page(() => import("./pages/Placement.js")) },
          { path: "onboarding", lazy: page(() => import("./pages/Onboarding.js")) },
          { path: "onboarding/:caseId", lazy: page(() => import("./pages/OnboardingCase.js")) },
          { path: "exports", lazy: page(() => import("./pages/Exports.js")) },
          { path: "payroll", lazy: page(() => import("./pages/Payroll.js")) },
          { path: "invoicing", lazy: page(() => import("./pages/Invoicing.js")) },
          {
            path: "invoicing/:batchId",
            lazy: page(async () => ({
              default: (await import("./pages/Invoicing.js")).InvoiceBatchPage,
            })),
          },
          { path: "inbox", lazy: page(() => import("./pages/Inbox.js")) },
          {
            path: "inbox/:threadId",
            lazy: page(() => import("./pages/Inbox.js")),
          },
          { path: "sequences", lazy: page(() => import("./pages/Sequences.js")) },
          { path: "vms", lazy: page(() => import("./pages/Vms.js")) },
          { path: "privacy", lazy: page(() => import("./pages/Privacy.js")) },
          { path: "admin/support", lazy: page(() => import("./pages/SupportBilling.js")) },
          { path: "privacy/:requestId", lazy: page(() => import("./pages/PrivacyRequest.js")) },
          { path: "payroll/:batchId", lazy: page(() => import("./pages/PayrollBatch.js")) },
          { path: "reports", lazy: page(() => import("./pages/Reports.js")) },
        ],
      },
    ],
  },
];
